import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export type PillVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'muted';

const VARIANTS: Record<PillVariant, string> = {
  neutral: 'bg-paper-2 text-ink-1',
  info: 'bg-status-info-tint text-status-info',
  success: 'bg-status-ok-tint text-status-ok',
  warning: 'bg-status-warn-tint text-status-warn',
  danger: 'bg-status-err-tint text-status-err',
  muted: 'bg-paper-2 text-ink-2',
};

export function Pill({
  variant = 'neutral',
  children,
  className,
}: {
  variant?: PillVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-token-pill px-2 py-0.5 text-meta font-medium whitespace-nowrap',
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Invoice status → pill variant (spec Appendix E). */
export function invoiceStatusPill(status: string): {
  variant: PillVariant;
  label: string;
} {
  switch (status) {
    case 'draft':
      return { variant: 'neutral', label: 'Kladde' };
    case 'postponed':
      return { variant: 'warning', label: 'Udskudt' };
    case 'approved':
      return { variant: 'success', label: 'Godkendt' };
    case 'sent':
      return { variant: 'info', label: 'Sendt' };
    case 'paid':
      return { variant: 'success', label: 'Betalt' };
    case 'cancelled':
      return { variant: 'muted', label: 'Annulleret' };
    default:
      return { variant: 'neutral', label: status };
  }
}

/** ClickUp task status (Danish names from the workspace) → pill. */
export function taskStatusPill(statusName: string): {
  variant: PillVariant;
  label: string;
} {
  const s = statusName.toLowerCase();
  if (/færdig|afsluttet|done|closed/.test(s)) return { variant: 'success', label: statusName };
  if (/afventer\s+kunde/.test(s)) return { variant: 'warning', label: statusName };
  if (/afventer\s+intern/.test(s)) return { variant: 'warning', label: statusName };
  if (/åben|open/.test(s)) return { variant: 'info', label: statusName };
  return { variant: 'neutral', label: statusName };
}
